<template>
  <div
    class="custom-slider"
    :class="{ opa4: props.disabled }"
    ref="slider"
    @click="handleClick"
    @mousedown="startDrag"
  >
    <div class="slider-track">
      <div
        class="slider-progress"
        :class="{ transition: progressWidthTransition }"
        :style="{ width: progressPercentage + '%' }"
      ></div>
      <div
        v-if="isControl"
        class="slider-thumb"
        :style="{ left: progressPercentage + '%' }"
        @mousedown.stop="startDrag"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, computed } from 'vue';

const props = defineProps({
  modelValue: { type: Number, default: 0 }, // 当前进度值 (0-100)
  min: { type: Number, default: 0 }, // 最小值
  max: { type: Number, default: 100 }, // 最大值
  step: { type: Number, default: 1 }, // 步长
  trackColor: { type: String, default: '#D9D9D9' }, // 轨道颜色
  progressColor: { type: String, default: '#2196f3' }, // 进度条颜色
  thumbColor: { type: String, default: '#ffffff' }, // 滑块颜色
  thumbSize: { type: Number, default: 14 }, // 滑块大小(px)
  trackWidth: { type: Number, default: 300 }, // 轨道宽度(px)
  trackHeight: { type: Number, default: 6 }, // 轨道高度(px)
  isControl: { type: Boolean, default: true }, // 是否可拖动
  disabled: { type: Boolean, default: false }, // 是否禁用
  /** 为 false 时始终无宽度缓动；为 true 时仅在进度值上升时使用缓动，下降或变为 0 时瞬时跳转 */
  isScrollingAnimation: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'change']);

const slider = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const progressPercentage = ref(0);
/** 仅在同一次变更中、且允许缓动时为 true，用于绑定 CSS transition */
const easeWidthThisStep = ref(false);

const progressWidthTransition = computed(
  () => props.isScrollingAnimation && easeWidthThisStep.value
);

// 计算百分比
const calculatePercentage = (value: any) => {
  return ((value - props.min) / (props.max - props.min)) * 100;
};

// 计算实际值
const calculateValue = (percentage: any) => {
  let value = props.min + (percentage / 100) * (props.max - props.min);
  // 应用步长
  if (props.step > 0) {
    value = Math.round(value / props.step) * props.step;
  }
  return Math.max(props.min, Math.min(props.max, value));
};

// 更新进度
const updateProgress = (clientX: any) => {
  if (!slider.value) return;

  const rect = slider.value?.getBoundingClientRect();
  let percentage = ((clientX - rect.left) / rect.width) * 100;
  percentage = Math.max(0, Math.min(100, percentage));

  const newValue = calculateValue(percentage);
  progressPercentage.value = calculatePercentage(newValue);

  emit('update:modelValue', newValue);
  emit('change', newValue);
};

// 点击跳转
const handleClick = (e: any) => {
  if (props.disabled) return;
  if (!props.isControl) return;
  if (isDragging.value) return;
  updateProgress(e.clientX);
};

// 开始拖动
const startDrag = (e: any) => {
  if (props.disabled) return;
  if (!props.isControl) return;
  isDragging.value = true;
  document.addEventListener('mousemove', handleDrag);
  document.addEventListener('mouseup', stopDrag);
  document.body.style.userSelect = 'none';
  updateProgress(e.clientX);
};

// 处理拖动
const handleDrag = (e: any) => {
  if (!isDragging.value) return;
  updateProgress(e.clientX);
};

// 停止拖动
const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDrag);
  document.body.style.userSelect = '';
};

// 监听 modelValue：仅递增时启用宽度缓动；递减、归零或无变化则瞬时更新
watch(
  () => props.modelValue,
  (newVal, oldVal) => {
    if (!props.isScrollingAnimation) {
      easeWidthThisStep.value = false;
    } else if (oldVal === undefined) {
      easeWidthThisStep.value = false;
    } else {
      const prev = Number(oldVal);
      const next = Number(newVal);
      if (next > prev) {
        easeWidthThisStep.value = true;
      } else {
        easeWidthThisStep.value = false;
      }
    }
    progressPercentage.value = calculatePercentage(newVal);
  },
  { immediate: true }
);

onUnmounted(() => {
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDrag);
});
</script>

<style lang="scss" scoped>
.custom-slider {
  position: relative;
  width: v-bind('props.trackWidth + "px"');
  height: v-bind('props.thumbSize + "px"');
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: calc((v-bind('props.thumbSize') - v-bind('props.trackHeight')) / 2) 0;
}

.slider-track {
  position: relative;
  width: 100%;
  height: v-bind('props.trackHeight + "px"');
  background-color: v-bind('props.trackColor');
  border-radius: v-bind('props.trackHeight / 2 + "px"');
}

.slider-progress {
  position: absolute;
  height: 100%;
  // background: linear-gradient(94deg, var(--bg-color-primary), #24d692);
  background: var(--bg-color-primary);
  border-radius: v-bind('props.trackHeight / 2 + "px"');
  pointer-events: none;
}

.transition {
  transition: all 1s;
}

.slider-thumb {
  position: absolute;
  width: v-bind('props.thumbSize + "px"');
  height: v-bind('props.thumbSize + "px"');
  background-color: v-bind('props.thumbColor');
  border-radius: 50%;
  box-shadow: 0 0 5px #a065ff;
  transform: translate(-50%);
  top: -50%;
  margin-top: calc(v-bind('props.thumbSize') / -2);
  pointer-events: none;
  transition: transform 0.1s;
  border: 2px solid #a065ff;
}

.custom-slider:active .slider-thumb,
.slider-thumb:active {
  box-shadow: 0 0 10px #a065ff;
  transform: translateX(-50%) scale(1.4);
}
</style>
